import { API_URL } from '../config';

/**
 * Create a Stripe checkout session
 * @returns {Promise<{url: string}>} Checkout session URL
 */
export async function createCheckoutSession() {
  try {
    // Determine backend URL: use API_URL if set, otherwise default to Render backend
    // In dev, API_URL should be empty to use proxy, in prod it should be the backend URL
    const isDev = process.env.NODE_ENV === 'development';
    let backendUrl = API_URL;
    
    // If API_URL is empty/undefined, use default backend in production
    if (!backendUrl && !isDev) {
      backendUrl = 'https://twodmvp.onrender.com';
    }
    
    // Build the full URL
    const url = backendUrl ? `${backendUrl}/api/create-checkout-session` : '/api/create-checkout-session';
    
    console.log('[Stripe API] Creating checkout session:', { 
      API_URL, 
      backendUrl, 
      url, 
      isDev,
      nodeEnv: process.env.NODE_ENV 
    });
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Request failed with status ${response.status}`);
    }

    const data = await response.json();
    // Return clientSecret for embedded checkout, or url for redirect checkout
    return data;
  } catch (error) {
    console.error('Stripe checkout error:', error);
    throw error;
  }
}

/**
 * Verify a Stripe checkout session
 * @param {string} sessionId - Stripe session ID
 * @returns {Promise<{paid: boolean, session_id: string}>}
 */
export async function verifySession(sessionId) {
  try {
    // Determine backend URL: use API_URL if set, otherwise default to Render backend
    // In dev, API_URL should be empty to use proxy, in prod it should be the backend URL
    const isDev = process.env.NODE_ENV === 'development';
    let backendUrl = API_URL;
    
    // If API_URL is empty/undefined, use default backend in production
    if (!backendUrl && !isDev) {
      backendUrl = 'https://twodmvp.onrender.com';
    }
    
    // Build the full URL
    const url = backendUrl ? `${backendUrl}/api/verify-session?session_id=${sessionId}` : `/api/verify-session?session_id=${sessionId}`;
    
    console.log('[Stripe API] Verifying session:', { 
      API_URL,
      backendUrl, 
      url, 
      isDev,
      nodeEnv: process.env.NODE_ENV 
    });
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Session verification error:', error);
    throw error;
  }
}

/**
 * Check if user has verified payment in current session
 * @returns {boolean}
 */
export function hasVerifiedPayment() {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem('payment_verified') === 'true';
}

/**
 * Clear payment verification (for testing or logout)
 */
export function clearPaymentVerification() {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem('payment_verified');
  sessionStorage.removeItem('stripe_session_id');
}

